import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InventoryResult } from '../api/AssistantApi';
import './DailySalesChart.css';

export default function DailySalesChart({ data }: { data: InventoryResult }) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [selected, setSelected] = useState('');
  const [metric, setMetric] = useState('unitsSold');
  const groups = Array.from(new Map(data.items.map(row => [`${row.companyId}:${row.depositId}`, `${row.companyName} · ${row.depositName}`])).entries());
  const key = groups.some(([value]) => value === selected) ? selected : groups[0]?.[0];
  const rows = data.items.filter(row => `${row.companyId}:${row.depositId}` === key);
  if (data.hasMore) return <p role="status">{t('dailySales.limited')}</p>;
  if (!rows.length) return <p>{t('chat.noResults')}</p>;
  const byDate = new Map(rows.map(row => [String(row.date), row]));
  const points: { date: string; value: number | null; missing: number }[] = [];
  const from = String(rows[0].from), to = String(rows[0].to);
  const start = Date.parse(`${from}T00:00:00Z`), end = Date.parse(`${to}T00:00:00Z`);
  for (let day = start; day <= end && points.length < 93; day += 86400000) {
    const date = new Date(day).toISOString().slice(0, 10), row = byDate.get(date);
    const value = row ? row[metric] : 0;
    points.push({ date, value: value === null || value === undefined ? null : Number(value), missing: Number(row?.missingAmounts || 0) });
  }
  const values = points.map(point => point.value).filter((value): value is number => value !== null && Number.isFinite(value));
  const max = Math.max(1, ...values), min = Math.min(0, ...values);
  const y = (value: number) => 16 + (max - value) / (max - min) * 145;
  const width = 320 / Math.max(points.length, 1);
  const format = (value: number | null) => value === null ? t('dailySales.unknown') : value.toLocaleString(i18n.resolvedLanguage || 'en', { maximumFractionDigits: 2 });
  return <section className="daily-sales-chart" aria-label={t('dailySales.title')}>
    <h4>{t('dailySales.title')}</h4>
    <label>{t('dailySales.series')}<select value={key} onChange={event => setSelected(event.target.value)}>{groups.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
    <label>{t('dailySales.metric')}<select value={metric} onChange={event => setMetric(event.target.value)}>
      {['unitsSold', 'recordedAmount', 'salesRows'].map(field => <option key={field} value={field}>{t(`chat.fields.${field}`)}</option>)}
    </select></label>
    <svg viewBox="0 0 400 205" role="img" aria-labelledby={`${id}-title ${id}-description`}>
      <title id={`${id}-title`}>{t('dailySales.title')} — {t(`chat.fields.${metric}`)} — {groups.find(([value]) => value === key)?.[1]}</title>
      <desc id={`${id}-description`}>{from} – {to}. {t('dailySales.tableHint')}</desc>
      <line x1="62" x2="384" y1={y(0)} y2={y(0)} stroke="#94a3b8" />
      <text x="57" y="19" textAnchor="end" fontSize="9">{format(max)}</text>
      <text x="57" y={y(min)} textAnchor="end" fontSize="9">{format(min)}</text>
      {points.map((point, index) => point.value === null ? <text key={point.date} x={62 + (index + .5) * width} y="174" textAnchor="middle" fontSize="10">?</text> :
        <rect key={point.date} x={62 + index * width + 1} y={Math.min(y(point.value), y(0))} width={Math.max(.5, width - 2)} height={Math.max(1, Math.abs(y(point.value) - y(0)))} rx="1" fill={point.missing && metric === 'recordedAmount' ? '#b7791f' : '#1976d2'}>
          <title>{point.date}: {format(point.value)}{point.missing ? ` · ${t('dailySales.missing')}` : ''}</title>
        </rect>)}
      <text x="62" y="194" fontSize="10">{from}</text><text x="383" y="194" textAnchor="end" fontSize="10">{to}</text>
    </svg>
    <p>{t('dailySales.coverage')}</p>
    {rows.some(row => Number(row.missingAmounts) > 0) && <p role="status">{t('dailySales.missing')}</p>}
    <details><summary>{t('dailySales.tableHint')}</summary><div className="assistant-table-scroll"><table>
      <thead><tr><th>{t('dailySales.date')}</th><th>{t(`chat.fields.${metric}`)}</th><th>{t('chat.fields.missingAmounts')}</th></tr></thead>
      <tbody>{points.map(point => <tr key={point.date}><td>{point.date}</td><td>{format(point.value)}</td><td>{point.missing}</td></tr>)}</tbody>
    </table></div></details>
  </section>;
}
